import { Box, Heading, Text, VStack, HStack, Alert, AlertIcon, UnorderedList, ListItem, Badge } from '@chakra-ui/react';
import MarketingLayout from '../components/marketing/MarketingLayout';
import CodeBlock from '../components/marketing/CodeBlock';

const envExample = `# server/.env
NODE_ENV=production
PORT=3059
DATABASE_URL=postgres://user:password@localhost:5432/statfalcon
JWT_SECRET=change-me-to-a-long-random-string
CLIENT_URL=https://your-domain.com

# Set to false for shared hosting / localhost DB connections
DATABASE_SSL=false`;

const Step = ({ n, title, children }) => (
  <Box>
    <HStack spacing={3} mb={3} mt={10}>
      <Box w={9} h={9} bgGradient="linear(135deg, brand.500, accent.500)" color="white" borderRadius="full" display="flex" alignItems="center" justifyContent="center" fontWeight="800" flexShrink={0}>{n}</Box>
      <Heading size="md" color="gray.900">{title}</Heading>
    </HStack>
    <Box pl={{ base: 0, md: 12 }}>{children}</Box>
  </Box>
);

export const InstallationPage = () => (
  <MarketingLayout title="Installation Guide" subtitle="Self-host StatFalcon on your own infrastructure. Prefer zero setup? Use Cloud instead — no installation required.">
    <VStack align="stretch" spacing={2}>
      <Alert status="info" borderRadius="xl">
        <AlertIcon />
        This guide is for the self-hosted license. Cloud accounts skip all of this — just add your tracking code.
      </Alert>

      <Step n="1" title="Requirements">
        <UnorderedList color="gray.700" spacing={1} lineHeight="1.8">
          <ListItem><strong>Node.js</strong> 18 or newer</ListItem>
          <ListItem><strong>PostgreSQL</strong> 13 or newer</ListItem>
          <ListItem>A server (small VPS is plenty for most sites) or Node-capable shared host</ListItem>
        </UnorderedList>
      </Step>

      <Step n="2" title="Get the files">
        <Text color="gray.700" lineHeight="1.8">
          Download your StatFalcon release from your license dashboard and upload it to the server, keeping the
          {' '}<Text as="code" fontFamily="mono">build/</Text> folder as a sibling of the <Text as="code" fontFamily="mono">server/</Text> folder.
        </Text>
      </Step>

      <Step n="3" title="Configure environment">
        <Text color="gray.700" lineHeight="1.8">Create <Text as="code" fontFamily="mono">server/.env</Text> with your database and secret values:</Text>
        <CodeBlock label="server/.env">{envExample}</CodeBlock>
      </Step>

      <Step n="4" title="Install dependencies">
        <Text color="gray.700" lineHeight="1.8">Install for both the app and the server (two separate installs):</Text>
        <CodeBlock label="shell">{'npm install\ncd server && npm install --production'}</CodeBlock>
      </Step>

      <Step n="5" title="Run database migrations">
        <Text color="gray.700" lineHeight="1.8">From the <Text as="code" fontFamily="mono">server/</Text> directory, create the schema:</Text>
        <CodeBlock label="shell">{'node db/migrate.js'}</CodeBlock>
        <Text color="gray.600" fontSize="sm">Then seed your first admin account using the seeds in <Text as="code" fontFamily="mono">server/db/seeds</Text>.</Text>
      </Step>

      <Step n="6" title="Build and start">
        <Text color="gray.700" lineHeight="1.8">
          In production the Express server serves the React build and the API together. Build the front end, then
          start the server behind a process manager such as pm2:
        </Text>
        <CodeBlock label="shell">{'npm run build\ncd server\nNODE_ENV=production node src/app.js'}</CodeBlock>
      </Step>

      <Step n="7" title="Add your tracking code">
        <Text color="gray.700" lineHeight="1.8">
          Log in, create a Site, and paste the generated tracking snippet into your pages. See the
          {' '}<Text as="span" color="brand.600" fontWeight="600">Documentation</Text> for the snippet and ad zone embeds.
        </Text>
      </Step>

      <Box mt={12} p={6} bg="brand.50" borderRadius="2xl" border="1px solid" borderColor="brand.100">
        <HStack mb={2}><Badge colorScheme="purple">Tip</Badge><Heading size="sm" color="gray.900">Updates &amp; support</Heading></HStack>
        <Text color="gray.700" lineHeight="1.8">
          Self-hosted licenses include 1 year of updates and support. Renew anytime to stay on the latest release —
          but your install keeps working whether or not you renew.
        </Text>
      </Box>
    </VStack>
  </MarketingLayout>
);

export default InstallationPage;
